import { notFound } from "next/navigation";

import {
  ApiErrorResponse,
  FetchConfig,
  Fetcher,
  FetchOptions,
} from "@/types/fetch";
import { ApiError, logError } from "@/utils/core/errors";

export const defaultOptions: FetchOptions = {
  headers: {
    "Content-Type": "application/json",
  },
};

type ResponseHandlerConfig = {
  withNextJsNotFoundRedirect?: boolean;
};

/**
 * Recover the question type the front end works in.
 *
 * The API reports Quantitative questions as Discrete — the two share one bin
 * convention, so nothing about the payload is translated — but the two are not
 * interchangeable for WRITING. The discrete edit form derives a step from the
 * bin count and shifts the bounds by half of it, the resolution modal restricts
 * input to grid-aligned values, and the group form submits a different field
 * set. Normalising once here keeps every one of those paths correct, instead of
 * each call site having to remember.
 *
 * Runs as a JSON.parse reviver rather than a second walk, so it costs nothing
 * beyond the parse that was already happening. `precision` is only ever set on
 * a Quantitative question, so a genuine Discrete one is never touched.
 */
const reviveNativeQuestionType = (_key: string, value: unknown): unknown => {
  if (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    (value as { type?: unknown }).type === "discrete" &&
    typeof (value as { precision?: unknown }).precision === "number"
  ) {
    (value as { type: string }).type = "quantitative";
  }
  return value;
};

export const handleResponse = async <T>(
  response: Response,
  config?: ResponseHandlerConfig
): Promise<T> => {
  const { withNextJsNotFoundRedirect } = config ?? {};

  if (!response.ok) {
    if (withNextJsNotFoundRedirect && response.status === 404) {
      return notFound();
    }

    let errorData: ApiErrorResponse;
    try {
      errorData = await response.json();
    } catch {
      errorData = {
        detail: "Unexpected Api Error",
      } as ApiErrorResponse;
    }

    const apiError = new ApiError(response, errorData);
    logError(apiError);

    throw apiError;
  }

  // Check the content type to determine how to process the response
  const contentType = response.headers.get("content-type");

  if (contentType && contentType.includes("application/zip")) {
    // If the response is a ZIP, return it as a Blob
    return response.blob() as unknown as T;
  }

  // Some endpoints might still have successful null response
  // So need to handle such cases
  const text = await response.text();
  if (!text) {
    return null as T;
  }

  try {
    return JSON.parse(text, reviveNativeQuestionType);
  } catch (error) {
    logError(error, {
      payload: text,
    });
    throw error;
  }
};

type FetchInitializer = <T>(
  url: string,
  options: FetchOptions,
  config?: FetchConfig
) => Promise<T>;

export function createFetcher(fetchInitializer: FetchInitializer): Fetcher {
  const get = <T>(
    url: string,
    options: FetchOptions = {},
    config?: FetchConfig
  ): Promise<T> => {
    return fetchInitializer<T>(url, { ...options, method: "GET" }, config);
  };

  const post = <T = Response, B = Record<string, unknown>>(
    url: string,
    body: B,
    options: FetchOptions = {},
    config?: FetchConfig
  ): Promise<T> => {
    const isFormData = body instanceof FormData;

    return fetchInitializer<T>(
      url,
      {
        ...options,
        method: "POST",
        body: isFormData ? body : JSON.stringify(body),
      },
      { emptyContentType: isFormData, ...config }
    );
  };

  const put = <T, B>(
    url: string,
    body: B,
    options: FetchOptions = {}
  ): Promise<T> => {
    return fetchInitializer<T>(url, {
      ...options,
      method: "PUT",
      body: JSON.stringify(body),
    });
  };

  const patch = <T, B>(
    url: string,
    body: B,
    options: FetchOptions = {}
  ): Promise<T> => {
    return fetchInitializer<T>(url, {
      ...options,
      method: "PATCH",
      body: JSON.stringify(body),
    });
  };

  const del = <T>(url: string, options: FetchOptions = {}): Promise<T> => {
    return fetchInitializer<T>(url, { ...options, method: "DELETE" });
  };

  return { get, post, put, patch, del };
}
